import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { store } from '@/store/store';
import { hubClient } from '@tfpl/api-client';
import { getConfig } from '@/config';

export interface User {
  id: number;
  name: string;
}

export interface UserVision {
  id: number;
  updatedAt: string;
  message: string;
  user: User;
  editor: User;
}

const transformUserVision = (vision: hubClient.CollaboratorVisionDto): UserVision | undefined => {
  if (vision == null) {
    return undefined;
  }
  return {
    id: vision.id,
    updatedAt: vision.updatedAt,
    message: vision.message,
    user: {
      id: vision.collaborator.id,
      name: vision.collaborator.name,
    },
    editor: {
      id: vision.editor.id,
      name: vision.editor.name,
    },
  };
};

@Module({ name: 'UserVisionModule', store, dynamic: true, namespaced: true })
export class UserVisionModule extends VuexModule {
  /**************************************************************************
   * STATE
   *************************************************************************/
  private _loading = false;
  private _error = false;
  private _userVision: UserVision | undefined = undefined;

  /**************************************************************************
   * GETTERS
   *************************************************************************/
  get userVision(): UserVision | undefined {
    return this._userVision;
  }

  get loading(): boolean {
    return this._loading;
  }

  get error(): boolean {
    return this._error;
  }

  /**************************************************************************
   * MUTATIONS
   *************************************************************************/
  @Mutation
  setUserVision(userVision: UserVision | undefined): void {
    this._userVision = userVision;
  }

  @Mutation
  setLoading(loading: boolean): void {
    this._loading = loading;
  }

  @Mutation
  setError(error: boolean): void {
    this._error = error;
  }

  /**************************************************************************
   * ACTIONS
   *************************************************************************/
  @Action({ rawError: true })
  public async fetchUserVision(collaboratorUuid: string): Promise<void> {
    this.setLoading(true);
    this.setUserVision(undefined);

    try {
      const { data: response } = await hubClient.V3CollaboratorVisionService.getVisionForCollaborator({
        path: { tenant: getConfig().TENANT, company: getConfig().TENANT, uuid: collaboratorUuid },
        throwOnError: true,
      });
      const userVision: UserVision | undefined = transformUserVision(response.data);
      this.setUserVision(userVision);
    } catch (err) {
      this.setError(true);
      console.log(err);
    } finally {
      this.setLoading(false);
    }
  }
}
export default getModule(UserVisionModule);
